/**
 * Re-scoping a saved chart spec, for a dashboard-wide period or breakdown.
 *
 * A **period** override is a field swap. A **breakdown** override is not: a
 * scored measure puts its breakdown in `rows` and a distribution puts it in
 * `columns`, so the same override produces a different shape depending on the
 * tile. That asymmetry is the whole reason this is one shared function rather
 * than a line of code in the dashboard — the results page and the dashboard
 * must build the identical spec from the same inputs, and if they ever stop
 * agreeing the failure is silent: a tile quietly showing a different chart.
 *
 * Client-only. The API runs whatever spec it is given and needs no copy.
 */
/** A measure that collapses the question into one figure. */
export const isScoredMeasure = (measure) => measure.kind === 'topBox' ||
    measure.kind === 'bottomBox' ||
    measure.kind === 'mean';
/**
 * Where a breakdown belongs, given the measure.
 *
 * `buildSpec` and `applyOverrides` both call this. Two implementations of the
 * same rule is exactly how a pinned tile and the card it came from drift
 * apart.
 */
export const placeBreakdown = ({ measure, chartedDimension, breakdown, }) => {
    if (isScoredMeasure(measure)) {
        // The question is already the measure, so the breakdown becomes the axis.
        return { rows: breakdown ? [breakdown] : [], columns: [] };
    }
    return {
        rows: chartedDimension ? [chartedDimension] : [],
        columns: breakdown ? [breakdown] : [],
    };
};
/**
 * The question a saved spec charts, recovered from the spec itself.
 *
 * This is what lets an override re-derive the dimension without the original
 * `ChartableField` — a dashboard holds specs, not the builder state they came
 * from.
 */
export const chartedFieldOf = (spec) => {
    if (isScoredMeasure(spec.measure)) {
        return spec.measure.field ?? null;
    }
    return spec.rows?.[0]?.field ?? null;
};
/**
 * Chart kinds that cannot carry a second dimension.
 *
 * A pie or donut is one circle: a breakdown would have to become a second
 * ring or a second chart, and either is a different picture from the one that
 * was pinned. Refused rather than quietly re-drawn as something else, because
 * silently showing a different shape is the class of bug the pinning work
 * existed to fix and a dashboard-wide control is an easy place to reintroduce
 * it.
 *
 * `stat` is deliberately NOT in this list. A single figure is a scored measure
 * that has no axis *yet*, and giving it one is the entire point of a scored
 * measure — `buildSpec` puts the breakdown in `rows` and draws a chart. Making
 * a stat refuse would mean the dashboard's breakdown control did nothing for
 * exactly the tiles it is most useful on. The anti-drift test caught this:
 * the results page charts the same question broken down, so a tile that
 * refused would disagree with it.
 */
const CANNOT_TAKE_A_BREAKDOWN = ['pie', 'donut'];
/** What a scored measure is drawn as once it gains an axis. */
const DEFAULT_SCORED_CHART = 'horizontalBar';
/**
 * @param resolveBreakdown turns a breakdown name into a dimension for this
 *   tile's template, or null when it names a question that template does not
 *   have. Passed in rather than imported so this stays testable on plain
 *   objects and free of the builder's field catalogue.
 */
export const applyOverrides = (spec, overrides, resolveBreakdown, 
/**
 * Whether this tile's survey can be filtered by that field at all. A
 * department applies to every template; a question only to the templates
 * that ask it. Passed in for the same reason `resolveBreakdown` is.
 */
canFilterBy = () => true) => {
    let next = spec;
    const applied = { dateRange: false, breakdown: false, crossFilter: false };
    let crossFilterRefusedBecause = null;
    let breakdownRefusedBecause = null;
    if (overrides.dateRange) {
        next = { ...next, dateRange: overrides.dateRange };
        applied.dateRange = true;
    }
    const wanted = overrides.breakdown;
    if (wanted) {
        const scored = isScoredMeasure(next.measure);
        const charted = chartedFieldOf(next);
        if (wanted === 'none') {
            // Removing a dimension can never make a chart incoherent, but a scored
            // measure with no axis left is a single figure and must say so.
            const placed = placeBreakdown({
                measure: next.measure,
                chartedDimension: next.rows[0] ?? null,
                breakdown: null,
            });
            next = {
                ...next,
                ...placed,
                visualization: scored
                    ? { ...next.visualization, kind: 'stat' }
                    : next.visualization,
            };
            applied.breakdown = true;
        }
        else if (CANNOT_TAKE_A_BREAKDOWN.includes(next.visualization.kind)) {
            breakdownRefusedBecause = 'A pie cannot show a second dimension.';
        }
        else {
            const dimension = resolveBreakdown(wanted, charted);
            if (!dimension) {
                breakdownRefusedBecause =
                    'This survey does not have the question the dashboard is broken down by.';
            }
            else {
                const placed = placeBreakdown({
                    measure: next.measure,
                    chartedDimension: next.rows[0] ?? null,
                    breakdown: dimension,
                });
                next = {
                    ...next,
                    ...placed,
                    visualization: scored && next.visualization.kind === 'stat'
                        ? { ...next.visualization, kind: DEFAULT_SCORED_CHART }
                        : next.visualization,
                };
                applied.breakdown = true;
            }
        }
    }
    const cross = overrides.crossFilter;
    if (cross && cross.values.length) {
        if (overrides.tileId &&
            cross.sourceTileId &&
            overrides.tileId === cross.sourceTileId) {
            // The tile the click came from keeps its full range of values, so it
            // still works as the selector.
            crossFilterRefusedBecause = null;
        }
        else if (!canFilterBy(cross.field)) {
            crossFilterRefusedBecause =
                'This survey does not ask the question the dashboard is filtered by.';
        }
        else {
            // Appended, never replacing: a tile pinned with its own filter keeps
            // it, and the two narrow together. Replacing would quietly widen a
            // tile that was pinned deliberately narrow.
            next = {
                ...next,
                filters: [
                    ...(next.filters ?? []),
                    { field: cross.field, op: 'anyOf', values: cross.values },
                ],
            };
            applied.crossFilter = true;
        }
    }
    return {
        spec: next,
        applied,
        breakdownRefusedBecause,
        crossFilterRefusedBecause,
    };
};
